import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { AuthService } from '../../core/auth/services/auth.service';
import { ApiService } from '../../core/http/services/api.service';

type VideoMetricType = 'first-frame' | 'playing' | 'rebuffered' | 'error' | 'quality-switch';

interface BrowserVideoMetric {
  type?: VideoMetricType;
  mode?: string;
  source?: string;
  elapsedMs?: number;
  durationMs?: number;
  height?: number;
  bitrate?: number;
  bandwidthEstimate?: number;
  mediaErrorCode?: number;
}

interface VideoPerformancePayload {
  type: VideoMetricType;
  mode: 'quality-first';
  source: string;
  elapsedMs: number;
  durationMs?: number;
  height?: number;
  bitrate?: number;
  bandwidthEstimate?: number;
  mediaErrorCode?: number;
  connectionType?: 'slow-2g' | '2g' | '3g' | '4g';
}

/**
 * Batches modal-player RUM events away from the playback path. Events are sent
 * only for signed-in users and only after the player already emitted its local
 * diagnostic event; failed telemetry is deliberately discarded.
 */
@Injectable({ providedIn: 'root' })
export class VideoPlaybackRumService {
  private static readonly batchSize = 20;
  private static readonly flushDelayMs = 2_000;

  private readonly document = inject(DOCUMENT);
  private readonly authService = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly pending: VideoPerformancePayload[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;

  init(): void {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    this.document.addEventListener('gulu:video-metric', this.onMetric as EventListener);
    window.addEventListener('pagehide', () => this.flush(), { passive: true });
  }

  private readonly onMetric = (event: CustomEvent<BrowserVideoMetric>): void => {
    // Anonymous viewers are excluded: this endpoint is authenticated and should
    // never make a playback attempt wait for a session check.
    if (!this.authService.isAuthenticated()) return;

    const metric = this.normalize(event.detail);
    if (!metric) return;

    this.pending.push(metric);
    if (this.pending.length >= VideoPlaybackRumService.batchSize) {
      this.flush();
      return;
    }

    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), VideoPlaybackRumService.flushDelayMs);
    }
  };

  private flush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.pending.length === 0 || !this.authService.isAuthenticated()) return;

    const metrics = this.pending.splice(0, VideoPlaybackRumService.batchSize);
    this.api.post<void>('/video-performance/metrics', { metrics }).subscribe({
      next: () => this.scheduleRemainingFlush(),
      // Diagnostics must never retry, show an error, or take bandwidth away from video.
      error: () => this.scheduleRemainingFlush(),
    });
  }

  private scheduleRemainingFlush(): void {
    if (this.pending.length === 0 || this.flushTimer) return;
    this.flushTimer = setTimeout(() => this.flush(), VideoPlaybackRumService.flushDelayMs);
  }

  private normalize(detail: BrowserVideoMetric | undefined): VideoPerformancePayload | null {
    if (!detail || detail.mode !== 'quality-first' || !this.isMetricType(detail.type)) return null;
    if (!this.isNumberInRange(detail.elapsedMs, 0, 120_000)) return null;

    const source = this.normalizeCloudflareSource(detail.source);
    if (!source) return null;

    const metric: VideoPerformancePayload = {
      type: detail.type,
      mode: 'quality-first',
      source,
      elapsedMs: detail.elapsedMs,
    };
    const durationMs = this.numberOrUndefined(detail.durationMs, 0, 120_000);
    const height = this.numberOrUndefined(detail.height, 0, 2_160);
    const bitrate = this.numberOrUndefined(detail.bitrate, 0, 100_000_000);
    const bandwidthEstimate = this.numberOrUndefined(detail.bandwidthEstimate, 0, 100_000_000);
    const mediaErrorCode = this.numberOrUndefined(detail.mediaErrorCode, 0, 10);
    const connectionType = this.connectionType();

    if (durationMs !== undefined) metric.durationMs = durationMs;
    if (height !== undefined) metric.height = height;
    if (bitrate !== undefined) metric.bitrate = bitrate;
    if (bandwidthEstimate !== undefined) metric.bandwidthEstimate = bandwidthEstimate;
    if (mediaErrorCode !== undefined) metric.mediaErrorCode = mediaErrorCode;
    if (connectionType) metric.connectionType = connectionType;
    return metric;
  }

  private normalizeCloudflareSource(rawSource: string | undefined): string | null {
    if (!rawSource) return null;
    try {
      // The playback listener intentionally removes the scheme/query string before
      // emitting its local event. Restore only an HTTPS scheme for server-side host
      // validation; the path remains free of signed-query data.
      const url = new URL(
        rawSource.startsWith('https://') ? rawSource : `https://${rawSource.replace(/^\/+/, '')}`,
      );
      const host = url.hostname.toLowerCase();
      if (!host.endsWith('.cloudflarestream.com') && !host.endsWith('.videodelivery.net')) return null;
      const source = `https://${host}${url.pathname}`;
      return source.length <= 512 ? source : null;
    } catch {
      return null;
    }
  }

  private connectionType(): VideoPerformancePayload['connectionType'] | undefined {
    const navigatorWithConnection = navigator as Navigator & {
      connection?: { effectiveType?: string };
    };
    const value = navigatorWithConnection.connection?.effectiveType;
    return value === 'slow-2g' || value === '2g' || value === '3g' || value === '4g' ? value : undefined;
  }

  private isMetricType(value: unknown): value is VideoMetricType {
    return value === 'first-frame' || value === 'playing' || value === 'rebuffered' || value === 'error' || value === 'quality-switch';
  }

  private isNumberInRange(value: unknown, min: number, max: number): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
  }

  private numberOrUndefined(value: unknown, min: number, max: number): number | undefined {
    return this.isNumberInRange(value, min, max) ? value : undefined;
  }
}
